// utils/presenceBeacon.js
//
// The presence signal behind Community -> Live Activity. This is NOT
// dashboard polling: it is the minimum a browser must send for "who is on
// Elakay right now" to be knowable at all. Per visible, recently-used tab:
// one tiny POST every 60s (server keeps it in RAM only, never MongoDB).
//   * nothing is sent while the tab is hidden, or after 5 min without any
//     interaction (a logged-in but idle person is not "active");
//   * hiding/closing the tab sends a "leave" so counts drop right away;
//   * visitorId (localStorage) is shared by the tabs of one browser, tabId
//     (sessionStorage) identifies one tab -- so several tabs never count as
//     several people.
//   * a login, logout or RoleSwitchDropdown change is reported at once (on
//     the next click/key, or instantly from another tab), not a minute later:
//     the daily Activity History reclassifies the person from that beacon.
//   * Individual Activity (only recorded when Family turned "Save Visitor
//     History" ON): each route change sends one "page" beacon carrying the
//     path WITHOUT query string or hash; the first beacon of a tab carries the
//     external referrer HOST; `i` says the tab saw a real interaction. A
//     browser with Global Privacy Control, or that opted out of analytics
//     (localStorage elakay_analytics_opt_out = "1"), sends `np` and is never
//     profiled -- it still counts in Live presence.
//   * when localStorage is unavailable the id only lives for this page load:
//     beacons then carry `eph` and the server never makes it a permanent
//     visitor record (it still counts in Live presence). Two tabs opened at
//     the same time converge on the id stored first (storage event).
import { elakay_API } from "../config";
import { isAuthenticated } from "../includes/auth";
import { getSelectedRoleOverride } from "../includes/pageInside/Rolle";

export const BEAT_MS = 60 * 1000;
export const IDLE_MS = 5 * 60 * 1000;
const VISITOR_KEY = "elakay_visitor_id";
const TAB_KEY = "elakay_tab_id";
const REFERRER_SENT_KEY = "elakay_tab_ref_sent";
export const ANALYTICS_OPT_OUT_KEY = "elakay_analytics_opt_out";

const randomId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
  }
};

const VALID_ID = /^[A-Za-z0-9_-]{8,64}$/;
// { id, persisted }: persisted is false when the storage refused the id.
const readOrCreateEntry = (storage, key) => {
  try {
    const existing = storage.getItem(key);
    if (existing && VALID_ID.test(existing)) return { id: existing, persisted: true };
    const created = randomId();
    storage.setItem(key, created);
    // Read back: another tab may have stored its id in the meantime.
    const stored = storage.getItem(key);
    return stored && VALID_ID.test(stored) ? { id: stored, persisted: true } : { id: created, persisted: false };
  } catch {
    return { id: randomId(), persisted: false }; // storage blocked: presence for this page load only
  }
};
const readOrCreate = (storage, key) => readOrCreateEntry(storage, key).id;

export const getPresenceIds = () => {
  const visitor = readOrCreateEntry(window.localStorage, VISITOR_KEY);
  return { visitorId: visitor.id, tabId: readOrCreate(window.sessionStorage, TAB_KEY), ephemeral: !visitor.persisted };
};

// Read-only accessor (sent with signin / signout), kept in its own module.
export { readPresenceVisitorId } from "./presenceVisitorId";

export const analyticsOptedOut = () => {
  try {
    if (navigator.globalPrivacyControl === true) return true;
    return window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

// External referrer host of this tab, sent once (first beacon of the tab).
const takeReferrerHost = () => {
  try {
    if (window.sessionStorage.getItem(REFERRER_SENT_KEY)) return "";
    window.sessionStorage.setItem(REFERRER_SENT_KEY, "1");
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    return host && host !== window.location.hostname ? host : "";
  } catch {
    return "";
  }
};

// One id per page load: with the router's location.key it makes a re-sent
// page beacon (React StrictMode double effect, retry) idempotent server-side.
const PAGE_LOAD_ID = randomId().slice(0, 12);
export const pathOnly = (path) => String(path || "/").split(/[?#]/)[0] || "/";

/**
 * Starts the presence signal; returns a stop() that removes every listener
 * and timer it created.
 */
export const startPresence = ({
  now = () => Date.now(),
  fetchImpl = (...a) => window.fetch(...a),
  getToken = () => isAuthenticated()?.token,
  getSelectedRole = () => getSelectedRoleOverride(),
  optedOut = analyticsOptedOut,
  takeReferrer = takeReferrerHost,
  // Scripted events (isTrusted false) never count as a human interaction.
  isRealInteraction = (event) => Boolean(event) && event.isTrusted !== false,
} = {}) => {
  const url = `${elakay_API}/presence/beacon`;
  const ids = getPresenceIds();
  let { visitorId } = ids;
  const { tabId, ephemeral } = ids;
  let lastInteraction = now();
  let announced = false; // are we currently counted as present?
  let lastIdentity = null; // token + switched role of the last beacon sent
  let lastIdentityCheck = 0;
  let interacted = false; // a real pointer/key/touch/scroll in this tab
  const identitySignature = () => `${getToken() || ""}|${getSelectedRole() || ""}`;
  const extras = () => {
    const out = {};
    if (interacted) out.i = 1;
    if (optedOut()) out.np = 1;
    if (ephemeral) out.eph = 1;
    return out;
  };

  const post = (payload) => {
    const token = getToken();
    return fetchImpl(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "text/plain", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ visitorId, tabId, ...payload, ...extras() }),
    }).catch(() => {});
  };

  const sendActive = () => {
    announced = true;
    lastIdentity = identitySignature();
    const ref = optedOut() ? "" : takeReferrer();
    post({ state: "active", ...(ref ? { ref } : {}) });
  };
  const sendLeave = () => {
    if (!announced) return;
    announced = false;
    const body = JSON.stringify({ visitorId, tabId, state: "leave", ...(ephemeral ? { eph: 1 } : {}) });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))) return;
    } catch { /* fall through */ }
    fetchImpl(url, { method: "POST", keepalive: true, headers: { "Content-Type": "text/plain" }, body }).catch(() => {});
  };

  const beat = () => {
    if (document.visibilityState !== "visible") return;
    if (now() - lastInteraction > IDLE_MS) { sendLeave(); return; }
    sendActive();
  };
  const onInteraction = (event) => {
    if (isRealInteraction(event)) interacted = true;
    const wasIdle = !announced && now() - lastInteraction > IDLE_MS;
    lastInteraction = now();
    if (wasIdle) { beat(); return; } // back from idle: counted again at once
    // Logged in / out or switched role since the last beacon: report it now
    // (checked at most once a second -- mousemove fires constantly).
    if (!announced || now() - lastIdentityCheck < 1000) return;
    lastIdentityCheck = now();
    if (identitySignature() !== lastIdentity) beat();
  };
  // Login/logout in another tab of this browser changes the shared token.
  // A first-visit race between tabs: adopt the browser id another tab stored
  // (only when this tab's id was persisted -- never replaces a stored id
  // with nothing).
  const onStorage = (event) => {
    if (event?.key === VISITOR_KEY && !ephemeral && event.newValue && VALID_ID.test(event.newValue) && event.newValue !== visitorId) {
      visitorId = event.newValue;
    }
    if (announced && identitySignature() !== lastIdentity) beat();
  };
  const onVisibility = () => { if (document.visibilityState === "visible") beat(); else sendLeave(); };
  const onPageHide = () => sendLeave();

  const events = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"];
  events.forEach((e) => window.addEventListener(e, onInteraction, { passive: true }));
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("storage", onStorage);
  const timer = setInterval(beat, BEAT_MS);
  beat();

  const stop = () => {
    clearInterval(timer);
    events.forEach((e) => window.removeEventListener(e, onInteraction));
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("storage", onStorage);
    sendLeave();
  };
  // One page view per route change (path only). Also counts as presence.
  stop.pageView = (path, locationKey = "") => {
    announced = true;
    lastIdentity = identitySignature();
    post({ state: "page", route: pathOnly(path), eid: `${PAGE_LOAD_ID}:${tabId.slice(0, 12)}:${String(locationKey).slice(0, 24)}` });
  };
  return stop;
};

export const presenceDisabled = () =>
  process.env.REACT_APP_PRESENCE_DISABLED === "true" || process.env.NODE_ENV === "test";
